import React, { useEffect, useState, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import { get, post, put } from './api';
import { t, LanguageSwitcher } from '@omnifuse/shared/frontend/i18n';
import { SettingsPanel } from '@omnifuse/shared/frontend/SettingsPanel';
import { get as apiGet, post as apiPost, put as apiPut } from './api';

// ═══ 样式 ═══
const S = {
  page: { maxWidth: 1100, margin: '0 auto', padding: 24, fontFamily: 'inherit' } as React.CSSProperties,
  card: { background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,.08)', marginBottom: 16 } as React.CSSProperties,
  grid4: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16 } as React.CSSProperties,
  btn: { padding: '6px 14px', borderRadius: 8, border: '1px solid #ccc', background: '#fff', cursor: 'pointer', fontSize: 13 } as React.CSSProperties,
  btnPrimary: { padding: '6px 14px', borderRadius: 8, border: 'none', background: '#1a73e8', color: '#fff', cursor: 'pointer', fontSize: 13 } as React.CSSProperties,
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 13 },
  th: { textAlign: 'left' as const, padding: '8px 10px', borderBottom: '2px solid #eee', color: '#555', whiteSpace: 'nowrap' as const },
  td: { padding: '8px 10px', borderBottom: '1px solid #f0f0f0' },
  tab: (active: boolean) => ({
    padding: '8px 18px', cursor: 'pointer', borderBottom: active ? '2px solid #1a73e8' : '2px solid transparent',
    color: active ? '#1a73e8' : '#555', fontWeight: active ? 600 : 400, fontSize: 14,
  }) as React.CSSProperties,
  input: { padding: '6px 10px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13, width: '100%' } as React.CSSProperties,
};

const STATUS_BADGE: Record<string, { label: string; color: string }> = {
  pending: { label: t('待处理'), color: '#999' },
  auto_approved: { label: t('✅ 自动批准'), color: '#188038' },
  conditional: { label: t('⚠️ 待补图'), color: '#e8710a' },
  manual_review: { label: t('🔍 转人工'), color: '#b06000' },
  rejected: { label: t('❌ 已拒绝'), color: '#d93025' },
  refunded: { label: t('💰 已退款'), color: '#1a73e8' },
  exchanged: { label: t('🔄 已换货'), color: '#7b1fa2' },
  closed: { label: t('已关闭'), color: '#666' },
};

const BAND_COLOR: Record<string, string> = { green: '#188038', yellow: '#e8710a', red: '#d93025', blacklist: '#000' };

function Badge({ status }: { status: string }) {
  const b = STATUS_BADGE[status] || { label: status, color: '#666' };
  return <span style={{ color: b.color, fontWeight: 600 }}>{b.label}</span>;
}

// ═══ 概览统计 ═══
function StatsView() {
  const [stats, setStats] = useState<any>(null);
  const [reasons, setReasons] = useState<any[]>([]);

  useEffect(() => {
    get('/api/v1/dashboard/stats').then((r: any) => setStats(r.data)).catch(() => {});
    get('/api/v1/dashboard/reasons').then((r: any) => setReasons(r.data || [])).catch(() => {});
  }, []);

  if (!stats) return <div style={S.card}>{t('加载中...')}</div>;

  const metric = (label: string, value: React.ReactNode, sub?: string) => (
    <div style={S.card}>
      <div style={{ fontSize: 12, color: '#888' }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, marginTop: 4 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: '#aaa', marginTop: 2 }}>{sub}</div>}
    </div>
  );

  return (
    <>
      <div style={S.grid4}>
        {metric(t('30天退货单'), stats.total_returns)}
        {metric(t('自动化率'), `${Math.round(stats.automation_rate * 100)}%`, t('无需人工介入的占比'))}
        {metric(t('欺诈拦截'), stats.fraud_blocked_count, `挽回 $${(stats.fraud_blocked_amount || 0).toFixed(0)}`)}
        {metric(t('已退款金额'), `$${(stats.refunded_amount || 0).toFixed(0)}`)}
      </div>
      <div style={S.card}>
        <h3 style={{ marginBottom: 12, fontSize: 15 }}>{t('退货原因分布（90 天）')}</h3>
        <table style={S.table}>
          <thead><tr><th style={S.th}>{t('原因')}</th><th style={S.th}>{t('数量')}</th><th style={S.th}>{t('金额')}</th></tr></thead>
          <tbody>
            {reasons.map((r: any) => (
              <tr key={r.reason_category}>
                <td style={S.td}>{r.reason_category}</td>
                <td style={S.td}>{r.cnt}</td>
                <td style={S.td}>${(r.amount || 0).toFixed(0)}</td>
              </tr>
            ))}
            {reasons.length === 0 && <tr><td style={S.td} colSpan={3}>{t('暂无数据')}</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ═══ 退货队列 ═══
function ReturnsView() {
  const [rows, setRows] = useState<any[]>([]);
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(() => {
    get(`/api/v1/returns${filter ? `?status=${filter}` : ''}`).then((r: any) => setRows(r.data || [])).catch(() => {});
  }, [filter]);

  useEffect(load, [load]);

  const act = async (id: string, action: string) => {
    setBusy(id);
    try {
      if (action === 'refund') await post(`/api/v1/returns/${id}/refund`);
      else await post(`/api/v1/returns/${id}/override`, { action });
      load();
    } catch (e: any) { alert(e.message); }
    setBusy('');
  };

  return (
    <div style={S.card}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {['', 'manual_review', 'auto_approved', 'conditional', 'rejected', 'refunded'].map(s => (
          <button key={s} style={{ ...S.btn, ...(filter === s ? { borderColor: '#1a73e8', color: '#1a73e8' } : {}) }} onClick={() => setFilter(s)}>
            {s === '' ? '全部' : (STATUS_BADGE[s]?.label || s)}
          </button>
        ))}
      </div>
      <table style={S.table}>
        <thead><tr>
          <th style={S.th}>{t('订单')}</th><th style={S.th}>{t('客户')}</th><th style={S.th}>{t('原因')}</th>
          <th style={S.th}>{t('金额')}</th><th style={S.th}>{t('欺诈分')}</th><th style={S.th}>{t('状态')}</th><th style={S.th}>{t('操作')}</th>
        </tr></thead>
        <tbody>
          {rows.map((r: any) => (
            <tr key={r.id}>
              <td style={S.td}>{r.order_name || r.order_id}</td>
              <td style={S.td}>{r.customer_email || '-'}</td>
              <td style={S.td}>{r.reason_category}</td>
              <td style={S.td}>${(r.requested_amount || 0).toFixed(0)}</td>
              <td style={{ ...S.td, color: r.fraud_score >= 60 ? '#d93025' : r.fraud_score >= 30 ? '#e8710a' : '#188038', fontWeight: 600 }}>
                {Math.round(r.fraud_score || 0)}
              </td>
              <td style={S.td}><Badge status={r.status} /></td>
              <td style={S.td}>
                {['manual_review', 'conditional', 'pending'].includes(r.status) && (
                  <span style={{ display: 'inline-flex', gap: 6 }}>
                    <button style={S.btnPrimary} disabled={busy === r.id} onClick={() => act(r.id, 'approve')}>{t('批准')}</button>
                    <button style={S.btn} disabled={busy === r.id} onClick={() => act(r.id, 'reject')}>{t('拒绝')}</button>
                  </span>
                )}
                {r.status === 'auto_approved' && (
                  <button style={S.btnPrimary} disabled={busy === r.id} onClick={() => act(r.id, 'refund')}>{t('执行退款')}</button>
                )}
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td style={S.td} colSpan={7}>{t('暂无退货申请')}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// ═══ 风险档案 ═══
function FraudView() {
  const [rows, setRows] = useState<any[]>([]);
  const [scanning, setScanning] = useState(false);

  const load = () => get('/api/v1/fraud/profiles').then((r: any) => setRows(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const scan = async () => {
    setScanning(true);
    try {
      const r: any = await post('/api/v1/fraud/scan');
      alert(`扫描完成：${r.data.scanned_orders} 订单，更新 ${r.data.profiles_updated} 个档案`);
      load();
    } catch (e: any) { alert(e.message); }
    setScanning(false);
  };

  const setBand = async (cid: string, band: string) => {
    try { await put(`/api/v1/fraud/profiles/${encodeURIComponent(cid)}/band`, { band }); load(); }
    catch (e: any) { alert(e.message); }
  };

  return (
    <div style={S.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
        <h3 style={{ fontSize: 15 }}>{t('客户风险档案')}</h3>
        <button style={S.btnPrimary} disabled={scanning} onClick={scan}>{scanning ? '扫描中...' : t('🔄 重新扫描订单')}</button>
      </div>
      <table style={S.table}>
        <thead><tr>
          <th style={S.th}>{t('客户')}</th><th style={S.th}>{t('订单数')}</th><th style={S.th}>{t('退货率')}</th>
          <th style={S.th}>INR</th><th style={S.th}>LTV</th><th style={S.th}>{t('风险分')}</th><th style={S.th}>{t('等级')}</th><th style={S.th}>{t('调整')}</th>
        </tr></thead>
        <tbody>
          {rows.map((r: any) => (
            <tr key={r.customer_id}>
              <td style={S.td}>{r.customer_email || r.customer_id}</td>
              <td style={S.td}>{r.total_orders}</td>
              <td style={S.td}>{Math.round((r.return_rate || 0) * 100)}%</td>
              <td style={S.td}>{r.inr_claims}</td>
              <td style={S.td}>${(r.ltv || 0).toFixed(0)}</td>
              <td style={S.td}>{Math.round(r.risk_score || 0)}</td>
              <td style={{ ...S.td, color: BAND_COLOR[r.risk_band] || '#666', fontWeight: 700 }}>{r.risk_band}</td>
              <td style={S.td}>
                <select value={r.risk_band} onChange={e => setBand(r.customer_id, e.target.value)} style={{ ...S.input, width: 'auto' }}>
                  <option value="green">green</option><option value="yellow">yellow</option>
                  <option value="red">red</option><option value="blacklist">blacklist</option>
                </select>
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td style={S.td} colSpan={8}>{t('暂无档案 — 点击"重新扫描订单"生成')}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// ═══ 政策设置 ═══
function PolicyView() {
  const [rules, setRules] = useState<any>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { get('/api/v1/policy').then((r: any) => setRules(r.data)).catch(() => {}); }, []);

  if (!rules) return <div style={S.card}>{t('加载中...')}</div>;

  const save = async () => {
    setSaving(true);
    try { await put('/api/v1/policy', rules); alert('已保存'); } catch (e: any) { alert(e.message); }
    setSaving(false);
  };

  const field = (label: string, key: string, type = 'number') => (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>{label}</label>
      <input
        style={S.input} type={type} value={rules[key] ?? ''}
        onChange={e => setRules({ ...rules, [key]: type === 'number' ? Number(e.target.value) : e.target.value })}
      />
    </div>
  );

  return (
    <div style={{ ...S.card, maxWidth: 520 }}>
      <h3 style={{ fontSize: 15, marginBottom: 16 }}>{t('退货政策规则')}</h3>
      {field(t('退货窗口（天）'), 'window_days')}
      {field(t('最终销售标签（逗号分隔）'), 'final_sale_tags', 'text')}
      {field(t('自动退款上限（$）'), 'auto_refund_max')}
      {field(t('需照片证明的原因（逗号分隔）'), 'require_photo_categories', 'text')}
      {field(t('欺诈转人工阈值（0-100）'), 'fraud_review_threshold')}
      {field(t('欺诈拒绝阈值（0-100）'), 'fraud_reject_threshold')}
      {field(t('挽留方案 LTV 门槛（$）'), 'retention_min_ltv')}
      <button style={S.btnPrimary} disabled={saving} onClick={save}>{saving ? '保存中...' : t('保存规则')}</button>
    </div>
  );
}

// ═══ 主应用 ═══
function App() {
  const [tab, setTab] = useState('returns');

  return (
    <div style={S.page}>
      <div style={{ position: 'fixed', top: 12, right: 16, zIndex: 1000 }}><LanguageSwitcher /></div>
      <h1 style={{ fontSize: 22, marginBottom: 4 }}>ReturnArbiter</h1>
      <p style={{ color: '#666', marginBottom: 16 }}>{t('AI 退货判责与欺诈筛查 — 自动判定 · Vision 核查 · 欺诈拦截')}</p>
      <div style={{ display: 'flex', borderBottom: '1px solid #eee', marginBottom: 20 }}>
        <div style={S.tab(tab === 'returns')} onClick={() => setTab('returns')}>{t('退货队列')}</div>
        <div style={S.tab(tab === 'stats')} onClick={() => setTab('stats')}>{t('洞察')}</div>
        <div style={S.tab(tab === 'fraud')} onClick={() => setTab('fraud')}>{t('风险档案')}</div>
        <div style={S.tab(tab === 'policy')} onClick={() => setTab('policy')}>{t('政策设置')}</div>
        <div style={S.tab(tab === 'settings')} onClick={() => setTab('settings')}>{t('设置')}</div>
      </div>
      {tab === 'returns' && <ReturnsView />}
      {tab === 'stats' && <StatsView />}
      {tab === 'fraud' && <FraudView />}
      {tab === 'policy' && <PolicyView />}
      {tab === 'settings' && <SettingsPanel api={{ get: apiGet, post: apiPost, put: apiPut }} />}
    </div>
  );
}

const root = createRoot(document.getElementById('root')!);
root.render(<App />);
